import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  SelectChangeEvent,
  Autocomplete,
  Chip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { Close as CloseIcon, LocationOn as LocationIcon } from '@mui/icons-material';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (projectData: ProjectFormData) => void;
  isLoading?: boolean;
}

export interface ProjectFormData {
  name: string;
  startDate: string;
  endDate: string;
  address: string;
  units: string;
  coordinates?: {
    latitude: number;
    longitude: number;
  };
}

interface AddressSuggestion {
  place_name: string;
  center: [number, number];
  context?: Array<{
    id: string;
    text: string;
  }>;
}

const NewProjectModal: React.FC<NewProjectModalProps> = ({ open, onClose, onSubmit, isLoading = false }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState<ProjectFormData>({
    name: '',
    startDate: '',
    endDate: '',
    address: '',
    units: '',
  });

  const [errors, setErrors] = useState<Partial<ProjectFormData>>({});
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const handleInputChange = (field: keyof ProjectFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value as string;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSelectChange = (field: keyof ProjectFormData) => (
    event: SelectChangeEvent<string>
  ) => {
    const value = event.target.value as string;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Geocoding function using Mapbox Geocoding API
  const searchAddresses = async (query: string) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }

    setIsSearching(true);
    try {
      const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
      if (!MAPBOX_ACCESS_TOKEN) {
        console.error('Mapbox access token not found');
        return;
      }

      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}`
      );

      if (response.ok) {
        const data = await response.json();
        setAddressSuggestions(data.features || []);
      } else {
        console.error('Geocoding request failed', response.status, response.statusText);
        setAddressSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching addresses:', error);
      setAddressSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddressSelect = (address: AddressSuggestion | null) => {
    if (address) {
      const [longitude, latitude] = address.center;
      
      // Only use the street address part
      const addressParts = address.place_name.split(', ');
      const streetAddress = addressParts[0] || '';

      setFormData(prev => ({
        ...prev,
        address: streetAddress,
        coordinates: {
          latitude,
          longitude
        }
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ProjectFormData> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Validate date range
    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      onSubmit(formData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      startDate: '',
      endDate: '',
      address: '',
      units: '',
    });
    setErrors({});
    setAddressSuggestions([]);
    setIsSearching(false);
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 3, 
        pb: 2,
        backgroundColor: '#FAFAFA',
        borderBottom: '1px solid #E0E0E0'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ 
            fontWeight: 600,
            color: theme.palette.text.primary,
            fontFamily: 'Montserrat, sans-serif'
          }}>
            Create New Project
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Project Name"
              value={formData.name}
              onChange={handleInputChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange('startDate')}
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange('endDate')}
              error={!!errors.endDate}
              helperText={errors.endDate}
              InputLabelProps={{ shrink: true }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              freeSolo
              options={addressSuggestions}
              filterOptions={(options) => options}
              getOptionLabel={(option) => 
                typeof option === 'string' ? option : option.place_name
              }
              loading={isSearching}
              onInputChange={(event, newInputValue) => {
                setFormData(prev => ({ ...prev, address: newInputValue }));
                searchAddresses(newInputValue);
              }}
              onChange={(event, newValue) => {
                handleAddressSelect(newValue as AddressSuggestion);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Street Address"
                  error={!!errors.address}
                  helperText={errors.address}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                    }
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">
                      {option.place_name.split(', ')[0]}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.place_name.split(', ').slice(1).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No addresses found"
              loadingText="Searching addresses..."
            />
          </Grid>



          <Grid item xs={12}>
            <FormControl fullWidth error={!!errors.units}>
              <InputLabel>Units in Project</InputLabel>
              <Select
                value={formData.units}
                label="Units in Project"
                onChange={handleSelectChange('units')}
                sx={{
                  borderRadius: '8px',
                }}
              >
                <MenuItem value="1-10">1-10 units</MenuItem>
                <MenuItem value="11-25">11-25 units</MenuItem>
                <MenuItem value="26-50">26-50 units</MenuItem>
                <MenuItem value="51-100">51-100 units</MenuItem>
                <MenuItem value="101-250">101-250 units</MenuItem>
                <MenuItem value="251-500">251-500 units</MenuItem>
                <MenuItem value="500+">500+ units</MenuItem>
              </Select>
              {errors.units && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.units}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Coordinates Display */}
          {formData.coordinates && (
            <Grid item xs={12}>
              <Box sx={{ 
                p: 2, 
                bgcolor: '#f5f5f5', 
                borderRadius: '8px',
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Project Coordinates
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`Latitude: ${formData.coordinates.latitude.toFixed(6)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Longitude: ${formData.coordinates.longitude.toFixed(6)}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  These coordinates will be used to center the map for this project.
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading}
          sx={{
            backgroundColor: '#337A5C',
            '&:hover': {
              backgroundColor: '#2a6350',
            },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
          }}
        >
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
              Creating...
            </>
          ) : (
            'Create Project'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewProjectModal;
